import { useCallback, useEffect, useMemo, useState } from "react";
import {
  assignDeviceToAccount,
  createAccountDevice,
  deassignDeviceById,
  deleteDeviceById,
  listAllDevices,
  updateDeviceById,
} from "../../api/endpoints/accountDevices.js";
import { listDeviceTypes } from "../../api/endpoints/deviceTypes.js";
import { listSims } from "../../api/endpoints/sims.js";
import { buildDeviceListSubtitle } from "../../pages/Devices/components/deviceUtils.js";

function useDeviceSelection(items, search, deviceTypeMap) {
  const [selectedId, setSelectedId] = useState(null);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((d) => {
      const typeName = deviceTypeMap[d.deviceTypeId]?.name ?? d.deviceType ?? "";
      const valueText = Object.values(d.values ?? {}).join(" ");
      return [d.deviceId, typeName, d.simType, d.simNumber, d.accountName, valueText]
        .some((v) => String(v ?? "").toLowerCase().includes(q));
    });
  }, [items, search, deviceTypeMap]);

  useEffect(() => {
    if (visible.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!visible.some((d) => d.id === selectedId)) {
      setSelectedId(visible[0].id);
    }
  }, [visible, selectedId]);

  const selected = useMemo(
    () => items.find((d) => d.id === selectedId) ?? null,
    [items, selectedId],
  );

  return { visible, selected, selectedId, setSelectedId };
}

export function useAllDevices() {
  const [items, setItems] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [sims, setSims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const deviceTypeMap = useMemo(
    () => Object.fromEntries(deviceTypes.map((type) => [type.id, type])),
    [deviceTypes],
  );

  const simMap = useMemo(
    () => Object.fromEntries(sims.map((sim) => [sim.id, sim])),
    [sims],
  );

  const { visible, selected, selectedId, setSelectedId } = useDeviceSelection(items, search, deviceTypeMap);

  const selectedDeviceType = useMemo(
    () => (selected?.deviceTypeId ? deviceTypeMap[selected.deviceTypeId] ?? null : null),
    [selected, deviceTypeMap],
  );

  const selectedSim = useMemo(
    () => (selected?.simId ? simMap[selected.simId] ?? null : null),
    [selected, simMap],
  );

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [data, types, simRows] = await Promise.all([listAllDevices(), listDeviceTypes(), listSims()]);
      setItems(data);
      setDeviceTypes(types);
      setSims(simRows);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const addDevice = useCallback(async (payload) => {
    setSaving(true);
    try {
      const { accountId, ...rest } = payload;
      const created = await createAccountDevice(accountId || null, rest);
      const all = await listAllDevices();
      setItems(all);
      setSelectedId(created.id);
      return created;
    } finally {
      setSaving(false);
    }
  }, []);

  const saveDevice = useCallback(async (id, payload) => {
    setSaving(true);
    try {
      await updateDeviceById(id, payload);
      const all = await listAllDevices();
      setItems(all);
    } finally {
      setSaving(false);
    }
  }, []);

  const removeDevice = useCallback(async (id) => {
    await deleteDeviceById(id);
    setItems((list) => list.filter((d) => d.id !== id));
  }, []);

  const deassignDevice = useCallback(async (id) => {
    setSaving(true);
    try {
      await deassignDeviceById(id);
      const all = await listAllDevices();
      setItems(all);
    } finally {
      setSaving(false);
    }
  }, []);

  const assignDevice = useCallback(async (deviceId, accountId) => {
    setSaving(true);
    try {
      await assignDeviceToAccount(deviceId, accountId);
      const all = await listAllDevices();
      setItems(all);
    } finally {
      setSaving(false);
    }
  }, []);

  const getDeviceTypeName = useCallback(
    (deviceTypeId) => deviceTypeMap[deviceTypeId]?.name ?? "—",
    [deviceTypeMap],
  );

  const getDeviceSubtitle = useCallback(
    (device) => buildDeviceListSubtitle(device, deviceTypeMap[device?.deviceTypeId]),
    [deviceTypeMap],
  );

  return {
    items: visible,
    allItems: items,
    deviceTypes,
    selectedDeviceType,
    sims,
    selectedSim,
    loading,
    search,
    setSearch,
    selected,
    selectedId,
    setSelectedId,
    addDevice,
    saveDevice,
    removeDevice,
    deassignDevice,
    assignDevice,
    saving,
    reload,
    getDeviceTypeName,
    getDeviceSubtitle,
  };
}
